import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { Badge, Box, Button, Collapsible, HStack, Input, Spinner, Text, VStack } from "@chakra-ui/react";
import { CandidateService } from "@/services/CandidateService";

const STATUS_COLORS = { Delivered: "green", Sent: "blue", Pending: "gray", Failed: "red" };

const WITHDRAWAL_CHANNELS = ["Email", "Phone", "Chat", "Other"];

const Row = ({ title, version, at, badge, details, extra }) => (
    <Box as="details" borderWidth="1px" borderRadius="md" px={3} py={2}>
        <Box as="summary" cursor="pointer" fontSize="sm">
            <HStack as="span" display="inline-flex" gap={2} flexWrap="wrap">
                <Text as="span" fontWeight="600">{title}</Text>
                <Text as="span" color="gray.600">{version} · {dayjs(at).format("DD.MM.YYYY HH:mm")}</Text>
                {badge}
            </HStack>
        </Box>
        {details && <Text mt={2} fontSize="sm" color="gray.700" whiteSpace="pre-wrap">{details}</Text>}
        {extra}
    </Box>
);

const WithdrawForm = ({ consentId, onDone }) => {
    const { t } = useTranslation("models");
    const [open, setOpen] = useState(false);
    const [channel, setChannel] = useState(WITHDRAWAL_CHANNELS[0]);
    const [note, setNote] = useState("");
    const [saving, setSaving] = useState(false);
    const [failed, setFailed] = useState(false);

    if (!open) {
        return <Button mt={2} size="xs" variant="outline" colorPalette="red" onClick={() => setOpen(true)}>{t("candidateDetailPage.consentWithdraw")}</Button>;
    }

    const submit = async () => {
        setSaving(true);
        setFailed(false);
        try {
            await CandidateService.withdrawConsent(consentId, { channel, note: note.trim() || null });
            onDone();
        } catch {
            setFailed(true);
        } finally {
            setSaving(false);
        }
    };

    return (
        <VStack align="stretch" gap={2} mt={2}>
            <Text fontSize="xs" color="gray.600">{t("candidateDetailPage.consentWithdrawHint")}</Text>
            <Box as="select" value={channel} onChange={(e) => setChannel(e.target.value)} borderWidth="1px" borderRadius="md" px={2} py={1} fontSize="sm" maxW="240px">
                {WITHDRAWAL_CHANNELS.map((c) => <option key={c} value={c}>{t(`candidateDetailPage.consentWithdrawChannel.${c}`)}</option>)}
            </Box>
            <Input size="sm" value={note} maxLength={2000} placeholder={t("candidateDetailPage.consentWithdrawNote")} onChange={(e) => setNote(e.target.value)} />
            {failed && <Text fontSize="xs" color="red.600">{t("candidateDetailPage.consentWithdrawFailed")}</Text>}
            <HStack gap={2}>
                <Button size="xs" colorPalette="red" loading={saving} onClick={submit}>{t("candidateDetailPage.consentWithdrawConfirm")}</Button>
                <Button size="xs" variant="ghost" onClick={() => setOpen(false)}>{t("candidateDetailPage.consentWithdrawCancel")}</Button>
            </HStack>
        </VStack>
    );
};

// Everything the candidate was told or agreed to, each with the accepted version and time.
const CandidateConsentRecords = ({ candidateId, onChanged }) => {
    const { t } = useTranslation("models");
    const [records, setRecords] = useState(undefined);
    const [reloadKey, setReloadKey] = useState(0);
    const [expanded, setExpanded] = useState(false);

    useEffect(() => {
        let cancelled = false;
        CandidateService.getConsentRecords(candidateId)
            .then((d) => { if (!cancelled) setRecords(d); })
            .catch(() => { if (!cancelled) setRecords(null); });
        return () => { cancelled = true; };
    }, [candidateId, reloadKey]);

    if (records === undefined) return <Spinner size="sm" color="purple.500" />;

    const consents = records?.consents ?? [];
    const disclosures = records?.aiDisclosures ?? [];
    if (!consents.length && !disclosures.length) {
        return <Text color="gray.600" fontSize="sm">{t("candidateDetailPage.consentsEmpty")}</Text>;
    }

    const withdrawn = consents.filter((c) => c.withdrawal).length;
    const summary = [
        t("candidateDetailPage.consentsSummary", { count: consents.length }),
        withdrawn ? t("candidateDetailPage.consentsSummaryWithdrawn", { count: withdrawn }) : null,
    ].filter(Boolean).join(" · ");

    return (
        <VStack align="stretch" gap={2}>
            <HStack justify="space-between" gap={2}>
                <Text fontSize="sm" color={withdrawn ? "red.600" : "gray.700"}>{summary}</Text>
                <Button size="xs" variant="ghost" onClick={() => setExpanded((v) => !v)}>
                    {t(expanded ? "candidateDetailPage.consentsHide" : "candidateDetailPage.consentsShow")}
                </Button>
            </HStack>
            <Collapsible.Root open={expanded}>
            <Collapsible.Content>
            <VStack align="stretch" gap={2} pt={1}>
            {disclosures.map((d) => (
                <Row
                    key={d.id}
                    title={t("candidateDetailPage.consentAiDisclosure")}
                    version={`${t(`candidateDetailPage.consentChannel.${d.channel}`, { defaultValue: d.channel })} · ${d.version}`}
                    at={d.sentAt}
                    badge={<Badge colorPalette={STATUS_COLORS[d.status] ?? "gray"}>{t(`candidateDetailPage.consentDelivery.${d.status}`, { defaultValue: d.status })}</Badge>}
                    details={d.failureReason ? `${d.text}\n\n${d.failureReason}` : d.text}
                />
            ))}
            {consents.flatMap((c) => {
                const w = c.withdrawal;
                const meta = `${t(`candidateDetailPage.consentChannel.${c.channel}`, { defaultValue: c.channel })} · #${c.contentHash.slice(0, 12)}`;
                const typeBadge = (type, extra) => (
                    <>
                        <Badge colorPalette={type === "consent" ? "purple" : "gray"}>{t(`candidateDetailPage.consentType.${type}`)}</Badge>
                        {extra}
                    </>
                );
                const withdrawnBadge = w && <Badge colorPalette="red">{t("candidateDetailPage.consentWithdrawn", { date: dayjs(w.withdrawnAt).format("DD.MM.YYYY HH:mm") })}</Badge>;
                const withdrawalInfo = w && (
                    <Text mt={2} fontSize="xs" color="red.600">
                        {t("candidateDetailPage.consentWithdrawnVia", { channel: t(`candidateDetailPage.consentWithdrawChannel.${w.channel}`, { defaultValue: w.channel ?? "-" }) })}
                        {w.note ? ` — ${w.note}` : ""}
                    </Text>
                );
                return [
                    <Row
                        key={`${c.id}-gdpr`}
                        title={t("candidateDetailPage.consentGdpr")}
                        version={`${c.gdprVersion} · ${meta}`}
                        at={c.acceptedAt}
                        badge={typeBadge("consent", withdrawnBadge)}
                        details={c.gdprText}
                        extra={w ? withdrawalInfo : <WithdrawForm consentId={c.id} onDone={() => { setReloadKey((k) => k + 1); onChanged?.(); }} />}
                    />,
                    ...(c.privacyPolicyText ? [
                        <Row
                            key={`${c.id}-privacy`}
                            title={t("candidateDetailPage.consentPrivacy")}
                            version={`${c.privacyPolicyVersion} · ${meta}`}
                            at={c.acceptedAt}
                            badge={typeBadge("information")}
                            details={c.privacyPolicyContent ? `${c.privacyPolicyText}\n\n${c.privacyPolicyContent}` : c.privacyPolicyText}
                        />,
                    ] : []),
                    ...(c.customConsents ?? []).map((x, i) => (
                        <Row
                            key={`${c.id}-custom-${i}`}
                            title={t("candidateDetailPage.consentCustom")}
                            version={`${x.version} · ${meta}`}
                            at={c.acceptedAt}
                            badge={typeBadge("consent", <>
                                <Badge colorPalette={x.accepted ? "green" : "gray"}>{t(x.accepted ? "candidateDetailPage.consentAccepted" : "candidateDetailPage.consentDeclined")}</Badge>
                                {x.accepted && withdrawnBadge}
                            </>)}
                            details={x.text}
                        />
                    )),
                ];
            })}
            </VStack>
            </Collapsible.Content>
            </Collapsible.Root>
        </VStack>
    );
};

export default CandidateConsentRecords;
